import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';

import {
  AuthService,
  BroadcastChannelService,
  CloudService,
  DerivedAuthService,
} from '@shared/lib/services';

import { environment } from '../environments/environment';
import { HealthSystemModalComponent } from './components/health-system-modal/health-system-modal.component';
import { AuthenticatedLayoutComponent } from './components/layouts/authenticated-layout/authenticated-layout.component';
import { RenderAffirmRegComponent } from './components/render-affirm-reg/render-affirm-reg.component';

declare const window: any;

@Component({
  selector: 'wu-root',
  imports: [
    RouterOutlet,
    AuthenticatedLayoutComponent,
    HealthSystemModalComponent,
    RenderAffirmRegComponent,
  ],
  templateUrl: './app.html',

  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly derivedAuthService = inject(DerivedAuthService);
  private readonly cloudService = inject(CloudService);
  private readonly isCurrentTab = document.visibilityState === 'visible';
  private chatWidgetLoaded = false;

  readonly isAuthenticated = this.authService.isAuthenticated;

  /** Check if chat=true query param is present - for standalone chat widget */
  readonly isChatWidgetMode = signal(this.checkChatWidgetMode());

  readonly e = effect(async () => {
    const message = this.broadcastChannelService.listen();
    if (!message || !message.type || this.isCurrentTab) return;

    if (message.type === 'LOGOUT') {
      sessionStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }

    if (message.type === 'AUTH_STATE_CHANGE') {
      window.location.reload();
    }
  });

  private readonly authTokenExpiryListener = (): void => {
    const email = this.derivedAuthService.currentUser()?.email;
    const uid = this.derivedAuthService.currentUser()?.uid;
    const studentId = this.derivedAuthService.studentId();
    const message = `${studentId} - reauthenticate action performed due to authTokenExpiry`;

    const stackData = {
      email,
      activity: 'reauthenticate',
      logType: 'ERROR',
      description: message,
    };
    this.cloudService.postDataToStack(
      environment.cloudFunction.baseUrl,
      stackData,
      () => {},
      console.error,
    );

    const auditData = {
      logType: 'INFO',
      email: email?.toLowerCase(),
      userId: uid,
      bannerId: studentId,
      userType: 'STUDENT',
      eventName: 'reauthenticate',
      activityStatus: 'AFTER EXECUTION',
      operationalStatus: 'ERROR',
      payLoad: message,
      comments: 'LOG INFO FOR reAuthenticate on authTokenExpiry',
      createdBy: 'Portal Refresh',
      eventType: 'authTokenExpiryHandle',
    };
    this.cloudService.postDataToBigQuery(
      environment.cloudFunction.baseUrl,
      auditData,
      () => {},
      console.error,
    );

    void this.reAuthenticate();
  };

  // =============================================
  // Private Methods (must be declared before public methods per ESLint)
  // =============================================

  private checkChatWidgetMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('chat') === 'true';
  }

  private getQueryParams(): { [key: string]: string } {
    const params: { [key: string]: string } = {};
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  private initChatWidget(): void {
    const params = this.getQueryParams();
    const studentId = params['user_id'] || '';
    const displayName = params['displayName'] || params['user_name'] || '';
    console.log(
      'Chat widget mode: Loading with student id -',
      studentId,
      displayName,
    );
    this.loadChatWidget(studentId, displayName);
  }

  private loadChatWidget(studentId: string, displayName: string): void {
    if (this.chatWidgetLoaded) {
      return;
    }

    // Load the Amazon Connect script
    const script = document.createElement('script');
    script.src =
      'https://dtn7rvxwwlhud.cloudfront.net/amazon-connect-chat-interface-client.js';
    script.async = true;
    script.id = '9708f925-5e4c-4b08-8523-30f57a552635';
    script.onload = () => this.centerChatWidget();
    document.head.appendChild(script);

    // Initialize the amazon_connect function
    window.amazon_connect =
      window.amazon_connect ||
      function (...args: any[]) {
        (window.amazon_connect.ac = window.amazon_connect.ac || []).push(args);
      };

    this.configureWidget(studentId, displayName);
    this.chatWidgetLoaded = true;
  }

  private configureWidget(studentId: string, displayName: string): void {
    // Styles
    window.amazon_connect('styles', {
      iconType: 'CHAT',
      openChat: {
        color: '#ffffff',
        backgroundColor: 'rgb(0, 101, 124)',
      },
      closeChat: {
        color: '#ffffff',
        backgroundColor: 'rgb(0, 101, 124)',
      },
    });

    // Custom Styles
    window.amazon_connect('customStyles', {
      global: {
        typeface: 'gill-sans-nova, Times, serif',
      },
      header: {
        headerTextColor: '#ffffff',
        headerBackgroundColor: '#00667D',
      },
      transcript: {
        messageFontSize: '13px',
        messageTextColor: '#000000',
        widgetBackgroundColor: '#ffffff',
        agentMessageTextColor: '#ffffff',
        systemMessageTextColor: '#ffffff',
        customerMessageTextColor: '#000000',
        agentChatBubbleColor: '#008EA9',
        systemChatBubbleColor: '#008EA9',
        customerChatBubbleColor: '#ebebeb',
      },
    });

    // Auto-launch behavior
    window.amazon_connect('customLaunchBehavior', {
      skipIconButtonAndAutoLaunch: true,
      alwaysHideWidgetButton: true,
    });

    // Snippet ID (your AWS Connect instance identifier)
    window.amazon_connect(
      'snippetId',
      'QVFJREFIaWFZYXRVSlpIekdkUUg5YXhZenVQMktKRXNIWTVFQWpBYVErTEdzRnpvZHdIL2g2bVBHZ0JTMjM3emRxajZtbnhYQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNaHl6dkExczdzcTA2QWJzcUFnRVFnQ3NzQmlmdkVOVFR5a0tGai96bStUTXJTT044dG1IOTN4dFdYR2NGTWdpdS96WGc5OTF2OCtycVVtVzY6OmhvL2FuT0kxdDA1YnhyZUhKWFJoNDVzR3VlME1qSEhmN25kc0dESGJCc1lVRXk2NFhaYTJ6ZGtnWWNEVVZTOXR6Z09HQ2lxWGMzS3RYNVZGODE3QWs2bVNWWHMrQkxUNFdlNTQ5Sy93elpRWEFZYW9tT0VDbEx1MnFQL2FjRDl2YVdCOXRGZHVsc3ZveVYzUC9qbWh6WFBEQjF3VExraz0=',
    );

    // Supported content types
    window.amazon_connect('supportedMessagingContentTypes', [
      'text/plain',
      'text/markdown',
      'application/vnd.amazonaws.connect.message.interactive',
      'application/vnd.amazonaws.connect.message.interactive.response',
    ]);

    // Display names
    window.amazon_connect('customDisplayNames', {
      transcript: {
        botMessageDisplayName: 'Virtual Agent',
      },
      header: {
        headerMessage: 'Live Chat',
      },
    });

    // Customer display name
    window.amazon_connect(
      'customerDisplayName',
      (callback: (name: string) => void) => {
        callback(displayName);
      },
    );

    window.amazon_connect('mockLexBotTyping', true);

    // Contact attributes
    window.amazon_connect('contactAttributes', {
      institutionAlias: 'waldenu249',
      studentId: studentId,
    });

    // Customization
    window.amazon_connect('customizationObject', {
      composer: {
        disableEmojiPicker: true,
        disableCustomerAttachments: true,
      },
    });

    this.hideCloseButton();
  }

  private hideCloseButton(): void {
    const interval = setInterval(() => {
      const closeBtn = document.getElementById(
        'amazon-connect-close-widget-button',
      );
      if (closeBtn) {
        closeBtn.style.display = 'none';
        clearInterval(interval);
      }
    }, 300);
  }

  private centerChatWidget(): void {
    const interval = setInterval(() => {
      const iframe = document.getElementById(
        'amazon-connect-widget-frame',
      ) as HTMLIFrameElement;
      if (iframe) {
        iframe.style.position = 'fixed';
        iframe.style.top = '50%';
        iframe.style.left = '50%';
        iframe.style.height = '640px';
        iframe.style.width = '100%';
        iframe.style.maxHeight = '100vh';
        iframe.style.maxWidth = '100vw';
        iframe.style.padding = '10px';
        iframe.style.transform = 'translate(-50%, -50%)';
        iframe.style.zIndex = '9999';
        clearInterval(interval);
      }
    }, 300);
  }

  // =============================================
  // Public Methods
  // =============================================

  ngOnInit(): void {
    // Check if this is a standalone chat widget mode
    if (this.isChatWidgetMode()) {
      this.initChatWidget();
      return; // Don't initialize other portal features
    }

    document.addEventListener(
      'authTokenExpiryHandle',
      this.authTokenExpiryListener,
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'authTokenExpiryHandle',
      this.authTokenExpiryListener,
    );
  }

  /**
   * Handles reauthentication when the authentication token expires.
   *
   * If a current user exists, attempts to reauthenticate using the
   * tenant ID from the current user authentication context. If no
   * current user is found, performs a logout instead.
   *
   * @returns A promise that resolves when reauthentication or logout completes.
   */
  async reAuthenticate(): Promise<void> {
    const tenantId =
      this.derivedAuthService.currentUserAuthentication()?.tenantId;
    if (this.derivedAuthService.currentUser()) {
      await this.authService.reauthenticate(tenantId ?? '');
    } else {
      await this.authService.logout();
    }
  }
}
